import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CategoryCard from './CategoryCard';
import { getCategories, getProducts, type Category } from '../lib/api';
import { apiUrl } from '../lib/config';
import { useCart } from '../state/cart.store';
import type { Product } from '../types';

type HeroData = {
    heroUrl?: string;
    heroType?: 'image' | 'video';
};

const Home: React.FC = () => {
    const { totalQuantity } = useCart();
    const [categories, setCategories] = useState<Category[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [hero, setHero] = useState<HeroData>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [cats, prods, settingsResp] = await Promise.all([
                    getCategories(),
                    getProducts(),
                    fetch(apiUrl('/api/settings/public')).then(r => r.json()).catch(() => ({})),
                ]);
                if (!cancelled) {
                    setCategories(cats);
                    setProducts(prods);
                    if (settingsResp?.heroUrl) {
                        setHero({ heroUrl: settingsResp.heroUrl, heroType: settingsResp.heroType });
                    }
                }
            } catch {
                // fallback silent
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const productCountByCategory = useMemo(() => {
        const map = new Map<string, number>();
        for (const p of products) {
            map.set(p.categorySlug, (map.get(p.categorySlug) ?? 0) + 1);
        }
        return map;
    }, [products]);

    return (
        <div>
            {hero.heroUrl && (
                <div className="hero-banner">
                    {hero.heroType === 'video' ? (
                        <video
                            className="hero-media"
                            src={hero.heroUrl}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                        />
                    ) : (
                        <img className="hero-media" src={hero.heroUrl} alt="Presentazione" />
                    )}
                </div>
            )}

            <div className="home-header">
                <div className="welcome">
                    <p>
                        Benvenuto! Seleziona una categoria e aggiungi i prodotti al carrello.
                        <br />
                        Pagamento alla consegna (Bologna e limitrofi).
                    </p>
                </div>
                <Link className="cart-link cart-badge-link" to="/cart">
                    Carrello {totalQuantity > 0 && <span className="cart-badge">{totalQuantity}</span>}
                </Link>
            </div>

            {loading ? (
                <p>Caricamento...</p>
            ) : (
                <div className="category-list">
                    {categories.map(cat => (
                        <CategoryCard key={cat.slug} category={cat} productCount={productCountByCategory.get(cat.slug) ?? 0} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Home;
